import { useCallback, useEffect, useRef, useState } from "react";
import type { FacebookConnectionResponse } from "@multi-chat/shared";
import { isApiError, isBackendUnavailable } from "../../api/client.js";
import {
  cancelPendingFacebook as cancelPendingApi,
  disconnectFacebook as disconnectApi,
  getFacebookConnection as getConnectionApi,
  selectFacebookPage as selectPageApi,
} from "../../api/facebook-api.js";

export type FacebookView =
  | { kind: "loading" }
  | { kind: "unavailable"; message: string }
  | { kind: "ready"; connection: FacebookConnectionResponse }
  | { kind: "error"; message: string; code?: string };

interface UseFacebookConnectionResult {
  view: FacebookView;
  refresh: () => void;
  selectPage: (pageId: string) => Promise<void>;
  cancelPending: () => Promise<void>;
  disconnect: () => Promise<void>;
  busy: boolean;
  actionError: string | null;
  clearActionError: () => void;
}

export function useFacebookConnection(
  organizationId: number,
  unreachableMessage: string,
): UseFacebookConnectionResult {
  const [view, setView] = useState<FacebookView>({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const orgIdRef = useRef(organizationId);
  orgIdRef.current = organizationId;
  const unreachableRef = useRef(unreachableMessage);
  unreachableRef.current = unreachableMessage;

  const clearActionError = useCallback(() => setActionError(null), []);

  const refresh = useCallback(() => setReloadToken((t) => t + 1), []);

  const toErrorView = useCallback((error: unknown): FacebookView => {
    if (isBackendUnavailable(error)) {
      return { kind: "unavailable", message: unreachableRef.current };
    }
    if (isApiError(error)) {
      return { kind: "error", message: error.message, code: error.code };
    }
    return { kind: "unavailable", message: unreachableRef.current };
  }, []);

  const toActionError = useCallback((error: unknown): string => {
    if (isBackendUnavailable(error)) return unreachableRef.current;
    if (isApiError(error)) return error.message;
    return unreachableRef.current;
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const currentOrgId = orgIdRef.current;
    setView({ kind: "loading" });
    setActionError(null);

    (async () => {
      try {
        const connection = await getConnectionApi(currentOrgId, controller.signal);
        if (controller.signal.aborted) return;
        setView({ kind: "ready", connection });
      } catch (error) {
        if (controller.signal.aborted) return;
        setView(toErrorView(error));
      }
    })();

    return () => controller.abort();
  }, [organizationId, reloadToken, toErrorView]);

  const selectPage = useCallback(
    async (pageId: string) => {
      setBusy(true);
      setActionError(null);
      try {
        const connection = await selectPageApi(orgIdRef.current, pageId);
        setView({ kind: "ready", connection });
      } catch (error) {
        setActionError(toActionError(error));
      } finally {
        setBusy(false);
      }
    },
    [toActionError],
  );

  const cancelPending = useCallback(async () => {
    setBusy(true);
    setActionError(null);
    try {
      const connection = await cancelPendingApi(orgIdRef.current);
      setView({ kind: "ready", connection });
    } catch (error) {
      setActionError(toActionError(error));
    } finally {
      setBusy(false);
    }
  }, [toActionError]);

  const disconnect = useCallback(async () => {
    setBusy(true);
    setActionError(null);
    try {
      const connection = await disconnectApi(orgIdRef.current);
      setView({ kind: "ready", connection });
    } catch (error) {
      setActionError(toActionError(error));
    } finally {
      setBusy(false);
    }
  }, [toActionError]);

  return {
    view,
    refresh,
    selectPage,
    cancelPending,
    disconnect,
    busy,
    actionError,
    clearActionError,
  };
}
